import { CountryResponse } from "./country.type";
import { FileType } from "./file.type";

export enum CompanyContactType {
  COMMERCIAL = "COMMERCIAL",
  FINANCIAL = "FINANCIAL",
  LEGAL_REPRESENTATIVE = "LEGAL_REPRESENTATIVE",
  QUALITY = "QUALITY",
  LOGISTICS = "LOGISTICS",
}

export interface CompanyResponse {
  createdAt: string;
  updatedAt: string;
  id: string;
  name: string;
  commercialName: string;
  taxId: string;
  email: string;
  lead: string | null;
  phone: string;
  country: CountryResponse;
  imageURL: string | null;
  type: string | null;
  deleted: boolean;
  active: boolean;
  addresses?: CompanyAddressResponse[];
}

export interface CompanyAddressResponse {
  id?: string;
  street: string;
  city: string;
  state: string;
  postalCode: string;
  countryId: string;
  country?: CountryResponse;
  isLegalAddress: boolean;
  isDeliverySite: boolean;
}

export interface CompanyContactResponse {
  id?: string;
  name: string;
  email: string;
  phone: string;
  position: string;
  type: CompanyContactType | "";
}

export interface CompanyCertificationRequest {
  certificationName: string;
  expirationDate: string;
  valid: boolean;
  fileURL: string;
}

export interface CompanyCertificationResponse {
  id: string;
  companyCertificationId: string;
  certificationName: string;
  expirationDate: string;
  valid: boolean;
  fileURL: string;
}

export interface CompanyMainClientRequest {
  clientName: string;
  contactEmail: string;
  contactPhone: string;
  contactPersonName: string;
}

export interface CompanyMainClientResponse {
  id: string;
  clientName: string;
  description: string;
  contactEmail: string;
  contactPhone: string;
  contactPersonName: string;
  createdAt: string;
  updatedAt: string;
}

export interface CompanyFileResponse {
  id: string;
  fileURL: string;
  name: string;
  description: string;
  type: FileType;
  /** ISO timestamp the file was uploaded (added for the Files table's "Uploaded" column). */
  createdAt?: string;
}

export interface CompanyFileRequest {
  fileURL: string;
  name: string;
  description: string;
  type: FileType | "";
}

export interface CompanyBankingInfoRequest {
  bankIdentifier: string;
  bankName: string;
  accountNumber: string;
  currency: string;
  accountType: string;
  swift?: string;
  iban?: string;
  routingNumber?: string;
  countryId?: string;
}

export interface CompanyBankingInfoResponse {
  id: string;
  bankIdentifier: string;
  bankName: string;
  accountNumber: string;
  currency: string;
  accountType: string;
  swift?: string;
  iban?: string;
  routingNumber?: string;
  countryId?: string;
}

export interface CompanyProfileRequest {
  companyId: string;
  name: string;
  website: string;
  taxId: string;
  commercialName: string;
  linkedinUrl: string;
  annualRevenue: number;
  taxDocuments: string;
  /** The logo's file key; empty removes it. */
  imageURL?: string;
  supplyCountryIds?: string[];
  addresses: CompanyAddressResponse[];
  contacts: CompanyContactResponse[];
  certifications: (
    | CompanyCertificationResponse
    | CompanyCertificationRequest
  )[];
  mainClients: (CompanyMainClientResponse | CompanyMainClientRequest)[];
  files: (CompanyFileResponse | CompanyFileRequest)[];
  bankingInfos: (CompanyBankingInfoResponse | CompanyBankingInfoRequest)[];
}

export interface CompanyProfileResponse {
  id: string;
  /** The logo's file key. */
  imageURL?: string;
  /** Where it says it can supply. Informational: nothing filters on it. */
  supplyCountries?: { id: string; name: string }[];
  name: string;
  taxId: string;
  email: string;
  phone: string;
  commercialName: string;
  website: string;
  linkedinUrl: string;
  annualRevenue: number;
  taxDocuments: string;
  addresses: CompanyAddressResponse[];
  contacts: CompanyContactResponse[];
  certifications: CompanyCertificationResponse[];
  mainClients: CompanyMainClientResponse[];
  files: CompanyFileResponse[];
  bankingInfos: CompanyBankingInfoResponse[];
  country: CountryResponse;
  type: string;
  active: boolean;
  deleted: boolean;
  createdAt: string;
  updatedAt: string;
}
